import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const markdownComponents = {
  a: ({ node, children, ...props }) => (
    <a {...props} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  ),
  table: ({ node, ...props }) => (
    <div className="ai-assistant-table-wrap">
      <table {...props} />
    </div>
  ),
};

const formatTime = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
};

/**
 * A single chat message. Assistant answers render as Markdown; user text is
 * rendered as plain text.
 */
const AssistantMessage = ({ message, onDownloadReport, downloadingReportId }) => {
  const isAssistant = message.role === 'assistant';
  const isError = message.status === 'error';
  const isPending = message.status === 'pending';
  const report = message.report;

  return (
    <li
      className={`ai-assistant-message ${isAssistant ? 'assistant' : 'user'} ${isError ? 'error' : ''}`}
    >
      <div className="ai-assistant-avatar" aria-hidden="true">
        <i className={`fa-solid ${isAssistant ? 'fa-robot' : 'fa-user'}`}></i>
      </div>
      <div className="ai-assistant-bubble-wrap">
        <div className="ai-assistant-meta">
          <span className="ai-assistant-author">{isAssistant ? 'AI Assistant' : 'You'}</span>
          {isAssistant && message.intentLabel && (
            <span className="ai-assistant-intent-badge">{message.intentLabel}</span>
          )}
          {message.createdAt && (
            <time className="ai-assistant-time" dateTime={message.createdAt}>
              {formatTime(message.createdAt)}
            </time>
          )}
        </div>
        <div className="ai-assistant-bubble">
          {isPending ? (
            <span className="ai-assistant-thinking" role="status">
              <i className="fas fa-spinner fa-spin" aria-hidden="true"></i> Analysing your
              records...
            </span>
          ) : isAssistant && !isError ? (
            <div className="ai-assistant-markdown">
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                {message.content}
              </ReactMarkdown>

              {report && (
                <div className="ai-assistant-report-card">
                  <div className="ai-assistant-report-header">
                    <div className="ai-assistant-report-icon-box" aria-hidden="true">
                      <i className="fa-solid fa-file-pdf"></i>
                    </div>
                    <div className="ai-assistant-report-info">
                      <div className="ai-assistant-report-tags">
                        <span className="ai-assistant-report-badge aspect">
                          {report.aspectLabel}
                        </span>
                        <span className="ai-assistant-report-badge period">
                          {report.periodLabel}
                        </span>
                        <span className="ai-assistant-report-badge format">PDF</span>
                      </div>
                      <h4 className="ai-assistant-report-title">{report.title}</h4>
                    </div>
                  </div>

                  {Array.isArray(report.kpis) && report.kpis.length > 0 && (
                    <div className="ai-assistant-report-kpis">
                      {report.kpis.slice(0, 4).map((kpi, idx) => (
                        <div key={idx} className="ai-assistant-report-kpi-pill">
                          <span className="ai-assistant-report-kpi-label">{kpi.label}</span>
                          <span className="ai-assistant-report-kpi-val">{kpi.value}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="ai-assistant-report-footer">
                    <button
                      type="button"
                      className="ai-assistant-report-download-btn"
                      onClick={() => onDownloadReport?.(report)}
                      disabled={downloadingReportId === report.id}
                      aria-label={`Download ${report.title} as PDF`}
                    >
                      {downloadingReportId === report.id ? (
                        <>
                          <i className="fas fa-spinner fa-spin" aria-hidden="true"></i>{' '}
                          Downloading...
                        </>
                      ) : (
                        <>
                          <i className="fa-solid fa-download" aria-hidden="true"></i> Download PDF
                          Report
                        </>
                      )}
                    </button>
                    {report.filename && (
                      <span className="ai-assistant-report-filename" title={report.filename}>
                        <i className="fa-regular fa-file" aria-hidden="true"></i> {report.filename}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p className="ai-assistant-plain">
              {isError && (
                <i
                  className="fas fa-exclamation-circle"
                  aria-hidden="true"
                  style={{ marginRight: 6 }}
                ></i>
              )}
              {message.content}
            </p>
          )}
        </div>
        {isAssistant && !isPending && message.scopeNotes && (
          <div className="ai-assistant-scope-note">
            <i className="fas fa-shield-halved" aria-hidden="true"></i> Based on{' '}
            {message.scopeNotes}
          </div>
        )}
      </div>
    </li>
  );
};

export default AssistantMessage;
